import { makeTinkerMessage } from '@convergence/lib';
import {
  ClientMessage,
  ConnectionCreatedMessage,
  CreateFolderMessageSchema,
  DeleteFileMessageSchema,
  DirectoryChangedMessage,
  DirectoryContentsMessage,
  SearchResultsMessageSchema,
  ServerMessage,
  SocketReadyMessage,
  TermStateMessageSchema,
  TinkerBoxFileOrDirectory
} from '@convergence/types';
import { FitAddon } from '@xterm/addon-fit';
import { SearchAddon } from '@xterm/addon-search';
import { SerializeAddon } from '@xterm/addon-serialize';
import { IDisposable, ITheme, Terminal } from '@xterm/xterm';
import axios from 'axios';
import { makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import { z } from 'zod';
import animation from '../assets/frames2.json';
import { Theme } from '../components/ThemeMaker/types';
import { keyboardEventMatches, KeyboardShortcut } from '../hooks/useShortcut';
import { API_ENDPOINT, refreshToken } from '../lib/api';
import { Telemetry } from '../lib/Telemetry';
import { WebSocketClient } from '../lib/WebSocketClient';

// function deepCopy<T>(obj: T): T {
//   return JSON.parse(JSON.stringify(obj));
// }
type TermStateMessage = z.infer<typeof TermStateMessageSchema>;

export const socketDataSchema = z.object({
  socketId: z.string(),
  expiresAt: z.number()
});

interface TinkerBoxConnectionArgs {
  socket: WebSocketClient;
  shortcutsToIgnore: KeyboardShortcut[];
}

class LoadingAnimation {
  terminal: Terminal;
  shouldCancel = false;
  constructor(terminal: Terminal) {
    this.terminal = terminal;
  }

  clearScreen() {
    this.terminal.write('\x1b[2J\x1b[H\n');
  }

  start() {
    // this.clearScreen();

    const width = this.terminal.cols;
    const height = this.terminal.rows;

    const animationWidth = animation.size.width;
    const animationHeight = animation.size.height;

    const paddingWidth = Math.max(0, width - animationWidth) / 2;
    const paddingHeight = Math.max(0, height - animationHeight) / 2;
    this.drawFrame(0, paddingWidth, paddingHeight);
  }

  drawFrame(index: number, paddingX: number, paddingY: number) {
    if (this.shouldCancel) {
      return;
    }
    const frame = animation.frames[Math.floor(index)];
    if (!frame) {
      return;
    }

    // set cursor to padding.x, padding.y
    this.terminal.write(
      `\x1b[${Math.floor(paddingY)};${Math.floor(paddingX)}H`
    );
    const lines = animation.frames[index]?.lines;
    if (!lines) {
      return;
    }
    for (const line of lines) {
      this.terminal.write(
        `\x1b[${Math.floor(paddingY + line.pos.y)};${Math.floor(
          paddingX + line.pos.x
        )}H`
      );
      this.terminal.write('\x1b[38;5;202m' + line.content + '\r');
    }
    if (this.shouldCancel) {
      return;
    } else {
      requestAnimationFrame(() => {
        if (this.shouldCancel) {
          return;
        }
        setTimeout(() => {
          this.drawFrame(
            (index + 1) % animation.frames.length,
            paddingX,
            paddingY
          );
        }, 50);
      });
    }
  }

  cancel() {
    this.shouldCancel = true;
  }
}

export const ConnectionState = {
  disconnected: 'disconnected',
  connecting: 'connecting',
  connected: 'connected'
} as const;

export type ConnectionState =
  (typeof ConnectionState)[keyof typeof ConnectionState];

type Timeout = ReturnType<typeof setTimeout>;

export class TinkerBoxConnectionStore {
  socket: WebSocketClient;
  tinkerBox: {
    id: number;
    name: string;
  } | null = null;
  terminal: Terminal;
  fitAddon: FitAddon;
  pingHandle = null as Timeout | null;
  serializeAddon = new SerializeAddon();
  searchAddon: SearchAddon;
  canWrite = false;
  error: string | null = null;
  currentDirectory = '';
  directoryContents: TinkerBoxFileOrDirectory[] = [];
  mounted = false;
  shortcutsToIgnore: KeyboardShortcut[];
  downloadUrl?: string;
  lineBuffer: Array<string | undefined> = [];
  history: string[] = [];
  shellListener: IDisposable;
  timeoutHandle: Timeout | null = null;
  syncPwd = false;
  connectionState: ConnectionState = ConnectionState.disconnected;
  socketReady = false;
  refreshState: 'refreshing' | 'refreshed' | 'idle' = 'idle';
  hasRecievedData = false;
  loadingAnimation: LoadingAnimation;

  constructor({ socket, shortcutsToIgnore }: TinkerBoxConnectionArgs) {
    makeAutoObservable(this);
    this.shortcutsToIgnore = shortcutsToIgnore;
    this.fitAddon = new FitAddon();
    this.terminal = new Terminal({
      cursorBlink: true,
      theme: TinkerBoxConnectionStore.themeToITheme(defaultThemeDark)
    });
    this.socket = socket;
    this.loadingAnimation = new LoadingAnimation(this.terminal);
    let socketUrl = `${API_ENDPOINT.replace('http', 'ws')}`;
    const socketData = this.getStoredSocket();
    if (socketData) {
      socketUrl += `?socketId=${socketData.socketId}`;
      Telemetry.additionalProperties({
        socketRestored: true,
        socketId: socketData.socketId
      });
    }
    // this.socket = new WebSocketClient({
    //   url: socketUrl
    // });

    this.searchAddon = new SearchAddon();
    this.loadingAnimation = new LoadingAnimation(this.terminal);
    this.terminal.loadAddon(this.searchAddon);
    this.terminal.loadAddon(this.fitAddon);
    this.terminal.loadAddon(this.serializeAddon);

    this.terminal.attachCustomKeyEventHandler((e) => {
      if (!this.canWrite) return false;
      for (const shortcut of this.shortcutsToIgnore || []) {
        if (keyboardEventMatches(e, shortcut)) {
          return false;
        }
      }
      return true;
    });

    // this.onSocketConnection = this.onSocketConnection.bind(this);
    this.onSocketReady = this.onSocketReady.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.logout = this.logout.bind(this);
    this.onServerData = this.onServerData.bind(this);
    this.onDisconnect = this.onDisconnect.bind(this);
    this.onError = this.onError.bind(this);
    this.onAuthError = this.onAuthError.bind(this);
    // this.connectionCreated = this.connectionCreated.bind(this)
    this.setTinkerBox = this.setTinkerBox.bind(this);
    this.onDirectoryContents = this.onDirectoryContents.bind(this);
    this.upload = this.upload.bind(this);
    this.setPwdSync = this.setPwdSync.bind(this);
    this.changeDirectoryToParent = this.changeDirectoryToParent.bind(this);
    this.getDirectoryContents = this.getDirectoryContents.bind(this);
    this.search = this.search.bind(this);
    this.changeRemoteDirectory = this.changeRemoteDirectory.bind(this);
    this.onLogout = this.onLogout.bind(this);
    this.onTinkerBoxConnectionEstablished =
      this.onTinkerBoxConnectionEstablished.bind(this);

    // Browser -> Backend
    this.shellListener = this.terminal.onData(this.onTerminaldata.bind(this));
  }

  startPing() {
    if (this.pingHandle) {
      clearInterval(this.pingHandle);
    }

    this.pingHandle = setInterval(() => {
      if (this.socket.readyState === WebSocket.OPEN) {
        this.socket.sendMessage({ event: ClientMessage.ping });
      }
    }, 5000);
  }

  stopPing() {
    if (!this.pingHandle) {
      return;
    }
  }
  getStoredSocket() {
    const socketData = sessionStorage.getItem('socket');
    if (socketData) {
      try {
        const parsed = socketDataSchema.parse(JSON.parse(socketData));
        return parsed;
      } catch (e) {
        console.error('Failed to parse socket data from localStorage', e);
      }
    }
  }

  setStoredSocket(socketId: string, expiresAt: Date) {
    sessionStorage.setItem(
      'socket',
      JSON.stringify({ socketId, expiresAt: expiresAt.getTime() })
    );

    Telemetry.additionalProperties({
      socketId,
      socketExpiresAt: expiresAt.toISOString()
    });
  }

  init(element: HTMLElement) {
    if (!this.mounted) {
      this.terminal.open(element);
      this.mounted = true;
    }
    this.syncPwd = this.getSyncPwdFromStorage();
    this.socketConnections();

    if (this.tinkerBox && this.connectionState !== ConnectionState.connected) {
      // the tinkerbox was set before the socket was initialized
      this.connectToTinkerBox();
      this.canWrite = true;
    }
  }

  socketConnections(): void {
    // this.socket.on(ServerMessage.connect, this.onConnect);
    // this.socket.onMessage(ServerMessage.connect, this.onSocketConnection);
    this.socket.onMessage(ServerMessage.socketReady, this.onSocketReady);
    this.socket.onClose(this.onDisconnect);
    this.socket.onMessage(
      ServerMessage.connectionCreated,
      this.onTinkerBoxConnectionEstablished.bind(this)
    );

    // Backend -> Browser
    this.socket.onMessage(ServerMessage.data, this.onServerData);
    this.socket.onMessage(ServerMessage.disconnect, this.onDisconnect);
    this.socket.onMessage(ServerMessage.logout, this.onLogout);
    this.socket.onMessage(ServerMessage.error, this.onError);
    this.socket.onMessage(
      ServerMessage.directoryContents,
      this.onDirectoryContents
    );
    this.socket.onMessage(
      ServerMessage.directoryChanged,
      this.onDirectoryChanged.bind(this)
    );
    this.socket.onMessage(
      ServerMessage.tinkerboxConnected,
      this.onTinkerBoxConnectionEstablished
    );
  }

  // onTinkerBoxConnectionEstablished() {
  //   this.connectionState = ConnectionState.connected;
  // }

  clearScreen() {
    this.terminal.write('\x1b[2J\x1b[H\n'); // clear screen
  }

  onTinkerBoxConnectionEstablished(data: unknown) {
    Telemetry.capture('tinker_box_connected');
    const parsed = ConnectionCreatedMessage.parse(data);
    this.connectionState = ConnectionState.connected;
    this.loadingAnimation?.cancel();
    this.clearScreen();
    if (parsed.termState) {
      this.loadSerializedTerminal(parsed.termState);
    }
    this.getDirectoryContents(parsed.defaultDirectory);
    this.timeoutHandle = setInterval(() => {
      if (this.connectionState === 'connected') {
        this.refreshDirectory();
      }
    }, 2000);
    this.startPing();
  }

  refreshDirectory() {
    this.getDirectoryContents(this.currentDirectory);
  }

  onDisconnect() {
    this.stopPing();
    this.connectionState = ConnectionState.disconnected;
    this.terminal.writeln(makeTinkerMessage('Connection lost :('));
    if (this.timeoutHandle) {
      clearInterval(this.timeoutHandle);
    }
  }

  onLogout() {
    this.connectionState = ConnectionState.disconnected;
    this.terminal.writeln(
      makeTinkerMessage('Connection closed: user logged out\r\n')
    );
    this.onLogoutCallback?.();
    this.stopPing();
    Telemetry.capture('tinker_box_logged_out');
  }

  onLogoutCallback: (() => void) | undefined = undefined;

  logout() {
    console.log('logging out...');
    if (this.timeoutHandle) {
      clearInterval(this.timeoutHandle);
    }
    this.socket.sendMessage({ event: ClientMessage.logout, data: {} });
  }

  disconnect() {
    this.socket.close();
  }

  async onAuthError() {
    if (this.refreshState !== 'refreshed') {
      this.refreshState = 'refreshing';
      await refreshToken();
      this.refreshState = 'refreshed';
    }
  }

  onError(err: unknown) {
    const e = z.string().parse(err);
    this.error = e;
    toast.error(e);
  }

  onDirectoryChanged(data: unknown) {
    const parsed = DirectoryChangedMessage.parse(data);
    this.currentDirectory = parsed.directory;
    this.directoryContents = parsed.contents;
  }

  setDarkMode(darkMode: boolean) {
    if (darkMode) {
      this.setTheme(defaultThemeDark);
    } else {
      this.setTheme(defaultThemeLight);
    }
  }

  deleteFile(path: string) {
    const validated = DeleteFileMessageSchema.parse({ path });
    this.socket.sendMessage({
      event: ClientMessage.deleteFile,
      data: validated
    });
  }

  createFolder(path: string) {
    const validated = CreateFolderMessageSchema.parse({ path });
    this.socket.sendMessage({
      event: ClientMessage.createFolder,
      data: validated
    });
  }

  static themeToITheme(theme: Theme): ITheme {
    return {
      background: theme[0].color,
      foreground: theme[1].color,
      black: theme[2].color,
      red: theme[3].color,
      green: theme[4].color,
      yellow: theme[5].color,
      blue: theme[6].color,
      magenta: theme[7].color,
      cyan: theme[8].color,
      white: theme[9].color,
      brightBlack: theme[10].color,
      brightRed: theme[11].color,
      brightGreen: theme[12].color,
      brightYellow: theme[13].color,
      brightBlue: theme[14].color,
      brightMagenta: theme[15].color,
      brightCyan: theme[16].color,
      brightWhite: theme[17].color,
      cursor: theme[1].color,
      selectionBackground: theme[9].color
    };
  }

  setTheme(theme?: Theme) {
    if (theme) {
      this.terminal.options.theme =
        TinkerBoxConnectionStore.themeToITheme(theme);
    }
  }

  changeDirectoryToParent() {
    const isSynced = this.syncPwd;
    const currentDirectory = this.currentDirectory;
    if (currentDirectory === '') {
      return;
    }
    const split = currentDirectory.split('/'); // ['', 'home', 'ubuntu']
    split.pop(); // ['', 'home']
    const newDirectory = split.join('/'); // '/home'
    this.getDirectoryContents(newDirectory);
    if (isSynced) this.changeRemoteDirectory(newDirectory);
  }

  changeRemoteDirectory(path: string) {
    this.currentDirectory = path;
    this.socket.sendMessage({
      event: ClientMessage.changeRemoteDirectory,
      data: { path }
    });
  }

  getDirectoryContents(path: string) {
    this.currentDirectory = path;
    this.socket.sendMessage({
      event: ClientMessage.directoryContents,
      data: { path }
    });
  }

  setTinkerBox(tinkerBox: { id: number; name: string }) {
    // If we are already connected to this tinker box, do nothing
    if (
      this.tinkerBox?.id === tinkerBox.id &&
      this.connectionState !== 'disconnected'
    ) {
      return;
    }
    this.tinkerBox = tinkerBox;

    // socket is not open yet
    if (this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    // if (!this.socketReady) {
    //   this.socket.sendMessage({
    //     event: ClientMessage.isSocketReady
    //   });
    // }
    this.canWrite = true;
    this.connectToTinkerBox();
  }

  connectToTinkerBox() {
    if (!this.tinkerBox) {
      return;
    }
    this.clearScreen();
    this.connectionState = ConnectionState.connecting;
    this.terminal.write(
      makeTinkerMessage(`Connecting to Tinkerbox '${this.tinkerBox.name}'...`)
    );
    this.loadingAnimation?.start();
    const { rows, cols } = this.terminal;
    this.socket.sendMessage({
      event: ClientMessage.tinkerBoxConnect,
      data: {
        tinkerBoxId: this.tinkerBox.id,
        rows,
        cols,
        syncPwd: this.syncPwd
      }
    });
  }

  onServerData(data: unknown) {
    if (!this.hasRecievedData) {
      // if we have not received any data yet, clear the screen so the loading animation is not visible
      this.hasRecievedData = true;
      // this.clearScreen();
    }
    this.terminal.write(z.string().parse(data), () => {
      this.saveSerializedTerminal();
    });
  }

  saveSerializedTerminal() {
    const serialized = this.serializeAddon.serialize({});
    const data: TermStateMessage = {
      termState: serialized
    };
    this.socket.sendMessage({
      event: ClientMessage.termState,
      data
    });
  }

  loadSerializedTerminal(state: string) {
    this.terminal.reset();
    this.terminal.write(state);
  }

  onDirectoryContents(data: unknown) {
    const validated = DirectoryContentsMessage.parse(data);
    this.directoryContents = validated;
  }

  resize() {
    this.fitAddon.fit();
    if (!this.terminal) {
      return;
    }
    const { rows, cols } = this.terminal;
    if (
      this.socket &&
      this.socket.readyState === WebSocket.OPEN &&
      this.connectionState === ConnectionState.connected
    ) {
      this.socket.sendMessage({
        event: ClientMessage.resize,
        data: { rows, cols }
      });
    }
    return { rows, cols };
  }

  async onSocketReady(message: unknown) {
    this.socketReady = true;
    try {
      const parsed = SocketReadyMessage.parse(message);
      console.log({ parsed });
      this.setStoredSocket(parsed.socketId, new Date());
    } catch (e) {}

    if (this.tinkerBox && this.connectionState !== ConnectionState.connected) {
      // the tinkerbox was set before the socket was initialized
      this.connectToTinkerBox();
      this.canWrite = true;
    }
  }

  onTerminaldata(data: unknown) {
    if (
      this.socket.readyState === WebSocket.OPEN &&
      this.connectionState === ConnectionState.connected
    ) {
      // if (newData === '\r') newData = '\r\n'
      this.socket.sendMessage({ event: ClientMessage.data, data });
    }
  }

  async upload(files: File[]): Promise<
    | {
        subscribe: (callbacks: {
          onProgress: (progress: number) => void;
          onComplete: () => void;
          onError: (error: Error) => void;
        }) => void;
      }
    | undefined
  > {
    const file = files?.[0];
    const tinkerBoxId = this.tinkerBox?.id;
    if (!tinkerBoxId) {
      return;
    }
    if (!file) {
      throw new Error('No file');
    }
    const sizeInBytes = file.size;
    const sizeInGb = sizeInBytes / (1024 * 1024 * 1024);
    if (sizeInGb > 1) {
      toast('File cannot be bigger than 1GB');
      throw new Error('File cannot be bigger than 1GB');
    }

    const form = new FormData();
    form.append('file', file);

    // const toastId = uuid();
    // toast(file.name, { progress: 0, toastId });

    const listeners: {
      onProgress: (progress: number) => void;
      onComplete: () => void;
      onError: (error: Error) => void;
    }[] = [];

    const res = axios.post(
      `${API_ENDPOINT}/tinker-box-connect/${tinkerBoxId}`,
      form,
      {
        params: {
          directory: this.currentDirectory,
          name: file.name
        },
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true,
        onUploadProgress: ({ loaded, total }: ProgressEvent) => {
          for (const listener of listeners) {
            const progress = Math.min(loaded / total, 0.95); // not complete until server responds complete
            listener.onProgress(progress);
          }
        }
      }
    );

    const self = this;
    res.then(() => {
      for (const listener of listeners) {
        listener.onComplete();
      }
      self.getDirectoryContents(self.currentDirectory);
    });

    return {
      subscribe(callbacks: {
        onProgress: (progress: number) => void;
        onComplete: () => void;
        onError: (error: Error) => void;
      }) {
        listeners.push(callbacks);
      }
    };
  }

  setPwdSync(syncPwd: boolean) {
    this.syncPwd = syncPwd;
    localStorage.setItem('followPwd', syncPwd ? 'true' : 'false');
    this.socket.sendMessage({
      event: ClientMessage.setPwdSync,
      data: { syncPwd }
    });
  }

  getSyncPwdFromStorage() {
    const sync = localStorage.getItem('followPwd');
    if (sync === null) return true;
    return sync === 'true';
  }

  // initNotebook() {
  //   this.noteBookData = {
  //     data: [{ results: 'initial data' }]
  //   };
  // }

  // updateNoteBookData() {
  //   if (!this.noteBookData) {
  //     return;
  //   }
  //   const fakeMessage = {
  //     code: 'alsdfkjasldfjasldfjasldjfasldfjas'
  //   };
  //   const stringified = JSON.stringify(fakeMessage);
  //   const parsed = JSON.parse(stringified);
  //   this.noteBookData.data[0]!.results = parsed.code;
  //   this.noteBookData = deepCopy(this.noteBookData);
  // }

  async search(query: string): Promise<TinkerBoxFileOrDirectory[] | null> {
    const message = JSON.stringify({ query });
    const eventName = `${ServerMessage.searchResults}:${query}`;
    const promise = new Promise<TinkerBoxFileOrDirectory[] | null>(
      (resolve) => {
        this.socket.onMessageOnce(eventName, (data) => {
          const validated = SearchResultsMessageSchema.parse(data);
          console.log('search results', validated, query);
          if (validated.query !== query) {
            return resolve(null);
          }
          resolve(validated.results);
        });
      }
    );
    this.socket.sendMessage({ event: ClientMessage.search, data: message });
    return promise;
  }
}

export type ProgressEvent = {
  lengthComputable: boolean;
  total: number;
  loaded: number;
};

export const defaultThemeLight: Theme = [
  { name: 'bg', color: '#FFFFFF' },
  { name: 'fg', color: '#2a2b00' },
  { name: 'c0', color: '#8e8f96' },
  { name: 'c1', color: '#da3e39' },
  { name: 'c2', color: '#FF8700' },
  { name: 'c3', color: '#315eee' },
  { name: 'c4', color: '#0e6fad' },
  { name: 'c5', color: '#930092' },
  { name: 'c6', color: '#92b279' },
  { name: 'c7', color: '#ED4D00' },
  { name: 'c8', color: '#000000' },
  { name: 'c9', color: '#da3e39' },
  { name: 'c10', color: '#FF8700' },
  { name: 'c11', color: '#315eee' },
  { name: 'c12', color: '#0e6fad' },
  { name: 'c13', color: '#930092' },
  { name: 'c14', color: '#2a2b32' },
  { name: 'c15', color: '#fffefe' }
];

export const defaultThemeDark: Theme = [
  { name: 'bg', color: '#171717' },
  { name: 'fg', color: '#cfd5e3' },
  { name: 'c0', color: '#2c3240' },
  { name: 'c1', color: '#af4b57' },
  { name: 'c2', color: '#92b279' },
  { name: 'c3', color: '#e5c078' },
  { name: 'c4', color: '#6e8fb3' },
  { name: 'c5', color: '#a3799d' },
  { name: 'c6', color: '#76b3c5' },
  { name: 'c7', color: '#dee3ec' },
  { name: 'c8', color: '#3b4356' },
  { name: 'c9', color: '#af4b57' },
  { name: 'c10', color: '#92b279' },
  { name: 'c11', color: '#e5c078' },
  { name: 'c12', color: '#6e8fb3' },
  { name: 'c13', color: '#a3799d' },
  { name: 'c14', color: '#7daeac' },
  { name: 'c15', color: '#e7eaf1' }
];
